import React, { useEffect, useState } from "react";
import { Box, Typography, Button } from '@mui/material';
import { DataGrid } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import AddIcon from '@mui/icons-material/Add';

// Components
import Copyright from '../internals/components/Copyright';
import RoleChip from "@component/RoleChip";
import SidePanel from "@component/SidePanel";
import AccountForm from "@component/AccountForm"; // 👈 Import view/update form
import AccountCreateForm from "@component/AccountCreateForm"; // 👈 Import create form

// Queries, Config & Utils
import { useAdminAccountListQuery } from "@query/accountQuery";
import { formatDateTimeWithoutSeconds } from "@utils/timeUtil";
import { pageSizeOptions } from "@config/config";

export default function AccountListPage() {
    const { t } = useTranslation(["main", "role", "account"]);

    // State for DataGrid
    const [users, setUsers] = useState(null);
    const [paginationModel, setPaginationModel] = useState({ pageSize: 25, page: 0 });

    // State for Side Panels
    const [detailPanelOpen, setDetailPanelOpen] = useState(false);
    const [selectedAccountUuid, setSelectedAccountUuid] = useState(null);
    const [createPanelOpen, setCreatePanelOpen] = useState(false);
    const [createFormKey, setCreateFormKey] = useState(Date.now());

    const adminAccountListQuery = useAdminAccountListQuery(paginationModel.page, paginationModel.pageSize);

    useEffect(() => {
        if (adminAccountListQuery.data?.data) {
            setUsers(adminAccountListQuery.data.data);
        }
    }, [adminAccountListQuery.data]);

    // --- Side Panel Handlers ---
    const handleDetailPanelClose = () => {
        setDetailPanelOpen(false);
        adminAccountListQuery.refetch();
    };

    const handleCreatePanelClose = () => {
        setCreatePanelOpen(false);
        setCreateFormKey(Date.now()); // Reset form by changing key
    };

    const handleCreateSuccess = (newAccountUuid) => {
        handleCreatePanelClose(); // Close and reset the create form
        adminAccountListQuery.refetch(); // Refresh the data grid

        // Open the detail panel for the newly created user
        if (newAccountUuid) {
            setSelectedAccountUuid(newAccountUuid);
            setDetailPanelOpen(true);
        }
    };

    const handleRowClick = (params) => {
        setSelectedAccountUuid(params.row.uuid);
        setDetailPanelOpen(true);
    };

    const columns = [
        { field: "email", headerName: t('E-mail'), flex: 1, sortable: false, filterable: false },
        { field: "first_name", headerName: t('firstName'), flex: 1, sortable: false, filterable: false },
        { field: "last_name", headerName: t('lastName'), flex: 1, sortable: false, filterable: false },
        {
            field: "role_name", headerName: t('role:role'), flex: 1, sortable: false, filterable: false,
            renderCell: (params) => <RoleChip roleApiName={params.formattedValue} t={t} />
        },
        {
            field: "created_on", headerName: t('creationDate'), flex: 1, sortable: false, filterable: false,
            valueFormatter: (params) => params ? formatDateTimeWithoutSeconds(params) : ''
        },
    ];

    return (
        <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' }, mx: 'auto' }}>

            {/* Side Panel for Viewing/Updating an Account */}
            <SidePanel open={detailPanelOpen} onClose={handleDetailPanelClose} title={t('account:accountDetails')}>
                <AccountForm uuid={selectedAccountUuid} onCancel={handleDetailPanelClose} />
            </SidePanel>

            {/* Side Panel for Creating a New Account */}
            <SidePanel open={createPanelOpen} onClose={handleCreatePanelClose} title={t('account:createAdminAccount')}>
                <AccountCreateForm key={createFormKey} onSuccess={handleCreateSuccess} onCancel={handleCreatePanelClose} />
            </SidePanel>

            <Box sx={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center", mb: 2 }}>

                <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography variant="h2" mb={1}>
                        {t('adminAccounts')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" mb={4}>
                        {t('account:pageSubtitle')}
                    </Typography>
                </Box>
                <Button
                    startIcon={<AddIcon />}
                    color="primary"
                    variant="contained"
                    onClick={() => setCreatePanelOpen(true)} // 👈 Open create panel
                >
                    {t("createNewUser")}
                </Button>
            </Box>

            <DataGrid
                rows={users?.items || []}
                columns={columns}
                rowCount={users?.pagination.total_items || 0}
                getRowId={(row) => row.uuid}
                paginationMode="server"
                onRowClick={handleRowClick} // 👈 Set row click handler
                loading={adminAccountListQuery.isFetching}
                autoHeight
                paginationModel={paginationModel}
                pageSizeOptions={pageSizeOptions}
                onPaginationModelChange={setPaginationModel}
                sx={{ '& .MuiDataGrid-row:hover': { cursor: 'pointer' } }}
            />

            <Copyright sx={{ my: 4 }} />
        </Box>
    );
}